import {pgettext} from "@/utils/translate";

export const BodyRegions = Object.freeze({
    HEAD: 'head',
    NECK: 'neck',
    THORAX: 'thorax',
    ARMS: 'arms',
    ABDOMEN: 'abdomen',
    PELVIS: 'pelvis',
    BACK: 'back',
    LEGS: 'legs',
});

export const BodyRegionsLabels = Object.freeze({
    [BodyRegions.HEAD]: pgettext('body_region', 'Head'),
    [BodyRegions.NECK]: pgettext('body_region', 'Neck'),
    [BodyRegions.THORAX]: pgettext('body_region', 'Thorax'),
    [BodyRegions.ARMS]: pgettext('body_region', 'Arms'),
    [BodyRegions.ABDOMEN]: pgettext('body_region', 'Abdomen'),
    [BodyRegions.PELVIS]: pgettext('body_region', 'Pelvis'),
    [BodyRegions.BACK]: pgettext('body_region', 'Back'),
    [BodyRegions.LEGS]: pgettext('body_region', 'Legs'),
});

const regionsReverseMap = new Map();
for (const [region, label] of Object.entries(BodyRegionsLabels)) {
    regionsReverseMap.set(label.toLowerCase(), region);
}

/**
 *
 * @param {string | null} label
 */
function getRegion(label) {
    if (label === null) {
        return null;
    }
    label = label.toLowerCase();
    if (!regionsReverseMap.has(label)) {
        return null;
    }
    return regionsReverseMap.get(label);
}

export class PredefinedDiagnosisList {
    /** @type {{[region: string]: string[]}} */
    regions = {};

    /**
     *
     * @param {{label: string, regions: string[]}[]} items
     */
    constructor(items) {
        for (const bodyRegion of Object.values(BodyRegions)) {
            this.regions[bodyRegion] = [];
        }
        for (let {name, bodyRegions} of items) {
            if (bodyRegions.length === 0) {
                bodyRegions = Object.values(BodyRegions);
            }
            for (const region of bodyRegions) {
                this.regions[region].push(name);
            }
        }
    }

    /**
     *
     * @param {string} region
     * @returns {string[]}
     */
    getForRegion(region) {
        return this.regions[region] || [];
    }
}

export class RegionDiagnosis {
    /** @type string[] */
    diagnoses = [];

    /** @type string[] */
    additionalNotes = [];

    get isEmpty() {
        return this.diagnoses.length === 0 && this.additionalNotes.length === 0;
    }

    hasDiagnosis(type) {
        return this.diagnoses.includes(type);
    }

    toggleDiagnosis(diagnosis) {
        if (this.hasDiagnosis(diagnosis)) {
            this.diagnoses = this.diagnoses.filter(t => t !== diagnosis);
        } else {
            this.diagnoses.push(diagnosis);
        }
    }

    get additionalNotesString() {
        return this.additionalNotes.join(', ');
    }

    set additionalNotesString(value) {
        this.additionalNotes = value.split(',').map(s => s.trim()).filter(s => s);
    }

    toString() {
        const result = [];
        for (const diagnosis of this.diagnoses) {
            result.push(diagnosis);
        }
        for (const note of this.additionalNotes) {
            result.push(note);
        }
        return result.join(', ');
    }
}

export class Diagnosis {
    /** @type {PredefinedDiagnosisList} */
    predefinedDiagnoses = null;

    /** @type {{[bodyRegion: string]: RegionDiagnosis}} */
    regions = {};

    /** @type string[] */
    notes = [];

    /**
     *
     * @param {PredefinedDiagnosisList} predefinedDiagnoses
     * @param {string | null} text
     */
    constructor(predefinedDiagnoses, text = null) {
        for (const bodyRegion of Object.values(BodyRegions)) {
            this.regions[bodyRegion] = new RegionDiagnosis();
        }
        this.predefinedDiagnoses = predefinedDiagnoses;

        if (text) {
            this._parseString(text);
        }
    }

    get notesString() {
        return this.notes.join('; ');
    }

    set notesString(value) {
        this.notes = value.split(';').map(s => s.trim()).filter(s => s);
    }

    _parseString(value) {
        const parts = value.split(';');
        for (let part of parts) {
            part = part.trim();
            if (!part) {
                continue;
            }

            let regionResult = /^(?<regionLabel>[^:]+):\s*(?<value>.+)$/.exec(part)
            if (regionResult !== null) {
                const {regionLabel, value} = regionResult.groups;
                const region = getRegion(regionLabel.trim());
                if (region !== null) {
                    this._parseRegionDiagnosis(region, value);
                    continue;
                }
            }

            this.notes.push(part);
        }
    }

    _parseRegionDiagnosis(region, value) {
        const regionPredefinedDiagnoses = this.predefinedDiagnoses.getForRegion(region);

        value = value.trim();
        const parts = value.split(',');
        for (let part of parts) {
            part = part.trim();
            if (!part) {
                continue;
            }

            if (regionPredefinedDiagnoses.includes(part)) {
                this.regions[region].diagnoses.push(part);
            } else {
                this.regions[region].additionalNotes.push(part);
            }
        }
    }

    toString() {
        let parts = [];
        for (const note of this.notes) {
            parts.push(note);
        }
        for (const [region, regionDiagnosis] of Object.entries(this.regions)) {
            if (regionDiagnosis.isEmpty) {
                continue;
            }
            parts.push(`${BodyRegionsLabels[region]}: ${regionDiagnosis.toString()}`);
        }
        return parts.join('; ');
    }
}
